import {
  ApiError,
  type CameraMedia,
  type CameraMediaKind,
  DEFAULT_CAMERA_CLIP_SECONDS,
  isTabletCameraMediaKind,
  MAX_CAMERA_CLIP_SECONDS,
  type MachineCamera,
  queryKeys,
  type VendingMachine,
} from '@mevaser/shared'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

/** A snapshot or clip needs the machine's Pi — or, on a glass cabinet, which
 *  has none, its checkout tablet; a photo or screen capture always needs the
 *  checkout tablet. */
function canAsk(m: VendingMachine, kind: CameraMediaKind): boolean {
  if (isTabletCameraMediaKind(kind) || m.kind === 'cabinet') return m.tabletDeviceId !== null
  return !!m.piDeviceId
}

/**
 * Asks a kiosk to take a snapshot or record a short clip from its camera — or
 * its checkout tablet to take a photo or capture its screen — then waits for
 * it. The capture is a row that starts `requested` and is filled in when the
 * device uploads, so this polls that row until it settles.
 * `machineId` fixes the machine (the machine page); without it the admin
 * picks one.
 */
export function CameraCaptureDialog({
  open,
  onOpenChange,
  machineId: fixedMachineId,
  onCaptured,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  machineId?: string
  onCaptured?: (media: CameraMedia) => void
}) {
  const queryClient = useQueryClient()
  const [machineId, setMachineId] = useState(fixedMachineId ?? '')
  const [cameraId, setCameraId] = useState('')
  const [kind, setKind] = useState<CameraMediaKind>('snapshot')
  const [seconds, setSeconds] = useState(String(DEFAULT_CAMERA_CLIP_SECONDS))
  const [captureId, setCaptureId] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setCaptureId(null)
      setCameraId('')
      setMachineId(fixedMachineId ?? '')
    }
  }, [open, fixedMachineId])

  const { data: machines } = useQuery({
    queryKey: queryKeys.vending.machines(),
    queryFn: () => api.get<VendingMachine[]>('/vending/machines'),
    enabled: open,
  })
  const tabletKind = isTabletCameraMediaKind(kind)
  const askable = (machines ?? []).filter((m) => m.status !== 'retired' && canAsk(m, kind))
  // Looked up in the full list, not `askable`: a fixed machine that cannot
  // take this kind of capture still needs to be found to say why.
  const machine = (machines ?? []).find((m) => m.id === machineId)
  const unsupported = machine !== undefined && !canAsk(machine, kind)

  const { data: cameras } = useQuery({
    queryKey: queryKeys.cameras.machineCameras(machineId),
    queryFn: () => api.get<MachineCamera[]>(`/vending/machines/${machineId}/cameras`),
    enabled: open && machineId !== '' && !tabletKind,
  })
  const usable = (cameras ?? []).filter((c) => c.enabled && (kind === 'snapshot' || c.streamUrl))
  const needsStream = kind === 'clip' && (cameras ?? []).some((c) => c.enabled && !c.streamUrl)

  const request = useMutation({
    mutationFn: () =>
      api.post<CameraMedia>('/cameras/captures', {
        machineId,
        ...(tabletKind ? {} : { cameraId }),
        kind,
        ...(kind === 'clip' ? { durationSeconds: Number(seconds) } : {}),
      }),
    onSuccess: (media) => {
      setCaptureId(media.id)
      void queryClient.invalidateQueries({ queryKey: queryKeys.cameras.all() })
    },
  })

  const { data: progress } = useQuery({
    queryKey: queryKeys.cameras.detail(captureId ?? ''),
    queryFn: () => api.get<CameraMedia>(`/cameras/media/${captureId}`),
    enabled: captureId !== null,
    refetchInterval: (q) => (q.state.data?.status === 'requested' || !q.state.data ? 2000 : false),
  })

  const status = progress?.status
  useEffect(() => {
    if (status === 'ready' || status === 'failed' || status === 'timed_out') {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cameras.all() })
    }
  }, [status, queryClient])

  const secondsValid =
    kind !== 'clip' ||
    (Number.isInteger(Number(seconds)) &&
      Number(seconds) >= 1 &&
      Number(seconds) <= MAX_CAMERA_CLIP_SECONDS)
  const canSubmit =
    machineId !== '' && (tabletKind || cameraId !== '') && secondsValid && !unsupported

  const waiting = captureId !== null && (!progress || progress.status === 'requested')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Capture from a kiosk</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {captureId === null ? (
            <>
              {!fixedMachineId ? (
                <Field label="Machine" required>
                  {(props) => (
                    <Select
                      value={machineId}
                      onValueChange={(value) => {
                        setMachineId(value)
                        setCameraId('')
                      }}
                    >
                      <SelectTrigger {...props}>
                        <SelectValue placeholder="Choose a machine…" />
                      </SelectTrigger>
                      <SelectContent>
                        {askable.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name}
                            {(tabletKind || m.kind === 'cabinet' ? m.tabletOnline : m.piOnline)
                              ? ''
                              : ' (offline)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              ) : null}

              <Field label="What to capture" required>
                {(props) => (
                  <Select
                    value={kind}
                    onValueChange={(value) => {
                      const next = value as CameraMediaKind
                      setKind(next)
                      setCameraId('')
                      // A machine picked for one kind may not suit the next.
                      if (!fixedMachineId && machine && !canAsk(machine, next)) setMachineId('')
                    }}
                  >
                    <SelectTrigger {...props}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="snapshot">Snapshot (a single picture)</SelectItem>
                      <SelectItem value="clip">Video clip</SelectItem>
                      <SelectItem value="photo">Customer photo (tablet front camera)</SelectItem>
                      <SelectItem value="screenshot">Tablet screen</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>

              {kind === 'clip' ? (
                <Field
                  label="Clip length (seconds)"
                  hint={`Up to ${MAX_CAMERA_CLIP_SECONDS} seconds.`}
                  error={
                    secondsValid
                      ? undefined
                      : `Enter a whole number from 1 to ${MAX_CAMERA_CLIP_SECONDS}`
                  }
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={1}
                      max={MAX_CAMERA_CLIP_SECONDS}
                      value={seconds}
                      onChange={(e) => setSeconds(e.target.value)}
                    />
                  )}
                </Field>
              ) : null}

              {tabletKind ? null : (
                <Field label="Camera" required>
                  {(props) => (
                    <Select
                      value={cameraId}
                      onValueChange={setCameraId}
                      disabled={machineId === ''}
                    >
                      <SelectTrigger {...props}>
                        <SelectValue
                          placeholder={
                            machineId === '' ? 'Choose a machine first' : 'Choose a camera…'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {usable.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              )}

              {tabletKind && kind === 'photo' ? (
                <Alert tone="info">
                  This photographs whoever is standing at the tablet. Viewing it is recorded in the
                  audit log.
                </Alert>
              ) : null}
              {unsupported ? (
                <Alert tone="warning">
                  {tabletKind || machine?.kind === 'cabinet'
                    ? 'This machine has no checkout tablet to take that.'
                    : 'This machine has no kiosk controller to take that.'}
                </Alert>
              ) : null}
              {!tabletKind && machineId !== '' && cameras && usable.length === 0 ? (
                <Alert tone="warning">
                  {needsStream
                    ? 'None of this machine’s cameras has an rtsp:// stream address yet, which clips need. Add one on the machine’s Cameras tab.'
                    : 'This machine has no enabled cameras. Add one on the machine’s Cameras tab.'}
                </Alert>
              ) : null}
              {machine &&
              !unsupported &&
              !(tabletKind ? machine.tabletOnline : machine.piOnline) ? (
                <Alert tone="warning">
                  {tabletKind
                    ? 'This kiosk’s tablet has not checked in recently, so it can’t take a picture until it does.'
                    : 'This kiosk’s controller is offline right now, so it can’t take a picture until it reconnects.'}
                </Alert>
              ) : null}
              {request.error ? (
                <Alert tone="danger">
                  {request.error instanceof ApiError
                    ? request.error.message
                    : 'Could not start the capture'}
                </Alert>
              ) : null}
            </>
          ) : waiting ? (
            <p className="py-6 text-center text-sm text-foreground-muted" role="status">
              Waiting for the kiosk…{' '}
              {kind === 'clip'
                ? 'A clip takes a little longer than a snapshot.'
                : tabletKind
                  ? 'The tablet picks up the request when it next checks in, which can take a couple of minutes.'
                  : ''}
            </p>
          ) : progress?.status === 'ready' ? (
            <Alert tone="success">Captured. It’s saved in the Cameras list.</Alert>
          ) : (
            <Alert
              tone="danger"
              title={
                progress?.status === 'timed_out'
                  ? 'The kiosk did not respond'
                  : 'The capture failed'
              }
            >
              {progress?.error ?? 'Try again in a moment.'}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          {captureId === null ? (
            <>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!canSubmit}
                loading={request.isPending}
                onClick={() => request.mutate()}
              >
                Capture
              </Button>
            </>
          ) : (
            <>
              {progress && progress.status !== 'requested' && progress.status !== 'ready' ? (
                <Button variant="secondary" onClick={() => setCaptureId(null)}>
                  Try again
                </Button>
              ) : null}
              {progress?.status === 'ready' ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    onCaptured?.(progress)
                    onOpenChange(false)
                  }}
                >
                  View
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                {waiting ? 'Hide' : 'Close'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
